import './ApiKeysList.scss';
import React, { useEffect, useState } from 'react';
import DataTable from 'src/component/UI/DataTable';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Tooltip } from '@mui/material';
import { getTablePagePreferences } from 'src/module/authentication/selector';
import { getApiKeys, getLoadingApiKeys, getNumApiKeysPages, getTotalApiKeys } from 'src/module/apiKeys/selector';
import { fetchApiKeys, deleteApiKey } from 'src/module/apiKeys/action';
import PermissionFence from 'src/component/PermissionFence';
import { useTranslation } from 'src/i18n';

// Mapping from role names to translation keys
const roleNameToTranslationKey: Record<string, string> = {
  'Administrator': 'users.administrator',
  'Regular User': 'users.regularUser',
  'Maintenance User': 'users.maintenanceUser',
  'EHT Specialist': 'users.ehtSpecialist',
  'Operations': 'users.operations',
};

export default function ApiKeysList (props: any = {}) {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const userPagePreference = useSelector(getTablePagePreferences('api-keys'));

  // Helper to translate a comma-separated list of role names
  const translateRoles = (permissionLevel: string): string => {
    if (!permissionLevel) return '';
    return permissionLevel
      .split(',')
      .map(role => role.trim())
      .map(role => {
        const translationKey = roleNameToTranslationKey[role];
        return translationKey ? t(translationKey) : role;
      })
      .join(', ');
  };

  const apiKeys: any = useSelector(getApiKeys);
  const numPages: any = useSelector(getNumApiKeysPages);
  const totalKeys: any = useSelector(getTotalApiKeys);
  const loading: any = useSelector(getLoadingApiKeys);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(userPagePreference);
  const [sort, setSort] = useState(null);

  const deleteRec = (e, id) => {
    e.stopPropagation();
    dispatch(deleteApiKey(id));
  };

  const columnOptions: any = [
    {
      id: 'name',
      Header: t('common.name'),
      accessor: (row: any) => row?.get('name') || '—',
      disableSortBy: true
    },
    {
      id: 'created_at',
      Header: t('apiKeys.createdAt'),
      accessor: (row: any) => row?.getIn(['created_at']) || '',
      sortable: true
    },
    {
      id: 'last_used_at',
      Header: t('apiKeys.lastUsedAt'),
      accessor: (row: any) => row.get('last_used_at') || t('common.never'),
      sortable: true
    },
    {
      id: 'permission_level',
      Header: t('apiKeys.permissionLevel'),
      accessor: (row: any) => translateRoles(row.get('permission_level')),
      disableSortBy: true
    },
    {
      id: 'actions',
      Header: '',
      accessor: (row: any) => row.id,
      disableSortBy: true,
      Cell: (row: any) => <div className='actions-container'>
        <PermissionFence can={['edit-api-key']} >
          <>
            <Tooltip title={t('apiKeys.editApiKey')} placement="top">
              <div onClick={(e) => {
                e.stopPropagation();
                if (props.onEdit) {
                  props.onEdit(row.row.original);
                }
              }}>
                <EditIcon className='edit-icon' style={{ marginRight: '8px', cursor: 'pointer' }} />
              </div>
            </Tooltip>
            <Tooltip title={t('apiKeys.deleteApiKey')} placement="top">
              <div onClick={(e) => deleteRec(e, row.row.original.get('id'))}>
                <DeleteIcon className='delete-icon'/>
              </div>
            </Tooltip>
          </>
        </PermissionFence>
      </div>
    }
  ];

  useEffect(() => {
    dispatch(fetchApiKeys({ paginationDetails: { page, pageSize, sort } }));
  }, [dispatch, page, pageSize, sort]);

  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setSort(_sort);
  };

  return (
    <div id='api-keys-list' className='api-keys-list' >
      <DataTable
        columns={columnOptions}
        data={apiKeys}
        pagination={true}
        numPages={numPages}
        totalRecords={totalKeys}
        loading={loading}
        fetchPage={fetchPage}
        tableId='api-keys'
        columnWidths={['25%', '20%', '20%', '25%', '10%']}
        hoverBorder={true}
      />
    </div>
  );
}

ApiKeysList.propTypes = {
  onEdit: PropTypes.func
};
